<script setup lang="ts">
import { storeToRefs } from 'pinia'

import CartCounter from './CartCounter.vue'
import CTAButton from './CTAButton.vue'
import InformationText from './InformationText.vue'
import InformationTextFull from './InformationTextFull.vue'
import ProPicture from './ProPicture.vue'

import DraftSavedMessage from '~/components/draft/autosave/DraftSavedMessage.vue'
import CampaignFooterPromoNudge from '~/components/ui/CampaignFooter/CampaignFooterPromoNudge.vue'
import GroovizAmount from '~/components/ui/GroovizAmount.vue'
import VText from '~/components/ui/VText.vue'

import { useNavigateToPreviousPage } from '~/composables/useNavigateToPreviousPage'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'
import { useSegmentTrackCampaign } from '~/composables/useSegmentTrackCampaign'

import { useBandSignupStore } from '~/stores/bandSignup'
import { useCartStore } from '~/stores/cart'
import { useCartPromoStore } from '~/stores/cartPromo'
import { useDraftStore } from '~/stores/draft'
import { useInfluencersStore } from '~/stores/influencers'
import { useInfluencersDuplicatesStore } from '~/stores/influencersDuplicates'
import { useInfluencersRecommendationsStore } from '~/stores/influencersRecommendations'
import { useMiscDraftStore } from '~/stores/miscDraft'
import { useMiscDraftProgressivePromosStore } from '~/stores/miscDraftProgressivePromos'
import { useMiscResizeStore } from '~/stores/miscResize'
import { useRoutingStore } from '~/stores/routing'
import { useSnackbarStore } from '~/stores/snackbar'
import { useUserStore } from '~/stores/user'
import { useUserBandStore } from '~/stores/userBand'

import pictureBuildHelper from '~/helpers/images/picture_build'

import { Breakpoints } from '~/enums/breakpoints'
import { DRAFT_STEP } from '~/enums/DraftStep'

import type { DraftState } from '~/stores/draft'
import type { Influencer, StatsV3Influencer } from '~/types/influencer'

const availablePageTypes = [
  'cart',
  'homepage',
  'profile',
  'sendtrack',
  'seoList',
  'shared-music',
] as const
export type AvailablePageTypes = (typeof availablePageTypes)[number]

type Props = {
  page: AvailablePageTypes
  draftId?: number
  influencer?: Influencer | StatsV3Influencer
  forceCTADisabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  draftId: 0,
  influencer: undefined,
  forceCTADisabled: false,
})

const route = useRoute()
const { t } = useI18n()
const { coreFetch } = useProvideCoreFetch()
const { trackCampaignInitiated, trackCuratorsSelectionValidated } =
  useSegmentTrackCampaign()
const localePath = useLocalePath()
const { navigateToPreviousPage } = useNavigateToPreviousPage()

const prosCount = ref(0)
const prosCountLoading = ref(false)

const {
  id: DRAFT_ID,
  draft_cost: DRAFT_COST,
  NEXT_PROGRESSIVE_PROMO_FROM_INF_COUNT: NEXT_PROMO,
  DRAFT_COST_AFTER_PROGRESSIVE_PROMO,
} = storeToRefs(useDraftStore())
const {
  price: CART_COST,
  CAMPAIGN_ITEM,
  CAMPAIGN_COST_AFTER_PROMO,
} = storeToRefs(useCartStore())
const { id: CART_PROMO_ID, IS_PROGRESSIVE_PROMO } = storeToRefs(
  useCartPromoStore(),
)
const { IS_ENABLED: IS_PROGRESSIVE_PROMOS_ENABLED } = storeToRefs(
  useMiscDraftProgressivePromosStore(),
)
const { displayDraftSaved: SHOULD_DISPLAY_DRAFT_SAVED } =
  storeToRefs(useMiscDraftStore())
const {
  influencers: DRAFT_INFLUENCERS,
  influencers_count: DRAFT_INFLUENCERS_COUNT,
} = storeToRefs(useDraftStore())
const { list: DUPLICATE_SUBMISSIONS } = storeToRefs(
  useInfluencersDuplicatesStore(),
)
const { mismatches: MISMATCHES, ranks: RANKED_INFLUENCERS } = storeToRefs(
  useInfluencersRecommendationsStore(),
)
const { PREVIOUS: PREVIOUS_URL } = storeToRefs(useRoutingStore())
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { IS_LOGGED_IN } = storeToRefs(useUserStore())
const { id: BAND_ID } = storeToRefs(useUserBandStore())

const { GET_BY_ID: GET_INF_BY_ID } = useInfluencersStore()
const { ADD_INF: ADD_INF_TO_CART, REMOVE_INF: REMOVE_INF_FROM_CART } =
  useDraftStore()
const { SET_DISPLAY: BAND_SIGNUP_SET_DISPLAY } = useBandSignupStore()
const { CHANGE_CAMPAIGN_FOOTER_DISPLAYED, SET_MISMATCH_MODAL } =
  useMiscDraftStore()
const { CREATE: CREATE_SNACKBAR } = useSnackbarStore()

const { MARK_STEP_DONE } = useDraftStore()
const { GET_NEXT_STEP_URL, GO_TO_NEXT_STEP } = useDraftFlowStore()

const isInfluencerDisabled = computed<boolean>(
  () => props.influencer?.is_scout || !props.influencer?.visible,
)
const currentProIsAdded = computed<boolean>(() =>
  Boolean(
    props.influencer && DRAFT_INFLUENCERS.value.includes(props.influencer.id),
  ),
)
const isDraftEmpty = computed<boolean>(
  () => DRAFT_INFLUENCERS_COUNT.value === 0,
)
const isDraftFilled = computed<boolean>(() => DRAFT_INFLUENCERS_COUNT.value > 0)
const isUserLoggedOut = computed<boolean>(() => !IS_LOGGED_IN.value)
const isCtaDisabled = computed<boolean>(
  () =>
    props.forceCTADisabled ||
    (props.page === 'sendtrack' && isDraftEmpty.value) ||
    (props.page === 'profile' && isInfluencerDisabled.value),
)

const ctaButtonKind = computed<string>(() => {
  switch (props.page) {
    case 'profile':
      return currentProIsAdded.value ? 'BtnOutlined' : 'BtnOrange'
    default:
      return 'BtnOrange'
  }
})

const ctaButtonText = computed<string>(() => {
  switch (props.page) {
    case 'homepage':
    case 'cart':
      if (props.draftId !== 0) {
        if (route.query.fromStep) {
          return SCREEN_WIDTH.value < Breakpoints.md
            ? t('components.campaignFooter.cta.back')
            : t('components.campaignFooter.cta.backToMyCampaign')
        } else {
          return SCREEN_WIDTH.value < Breakpoints.md
            ? t('components.campaignFooter.cta.next')
            : t('components.campaignFooter.cta.nextStep')
        }
      } else if (isDraftFilled.value) {
        return SCREEN_WIDTH.value < Breakpoints.md
          ? t('components.campaignFooter.cta.continue')
          : t('components.campaignFooter.cta.continueTheCampaign')
      } else {
        return t('components.campaignFooter.cta.startCampaign')
      }

    case 'shared-music':
      return t('components.campaignFooter.cta.browseAllCurators')
    case 'sendtrack':
      return SCREEN_WIDTH.value < Breakpoints.md
        ? t('components.campaignFooter.cta.next')
        : t('components.campaignFooter.cta.nextStep')

    case 'profile':
      if (isInfluencerDisabled.value) {
        return SCREEN_WIDTH.value >= Breakpoints.md
          ? t('components.campaignFooter.cta.disabledDesktop')
          : t('components.campaignFooter.cta.disabledMobile')
      } else if (isUserLoggedOut.value) {
        return props.influencer?.contest
          ? t('components.campaignFooter.cta.signUpToCandidate')
          : t('components.campaignFooter.cta.signUpToContact')
      } else if (DRAFT_INFLUENCERS.value.includes(props.influencer?.id ?? 0)) {
        return t('components.campaignFooter.cta.added')
      } else {
        const cost = Number(props.influencer?.cost)

        if (props.influencer?.contest) {
          if (cost) {
            return SCREEN_WIDTH.value < Breakpoints.md
              ? t('components.campaignFooter.cta.candidateForMobile')
              : t('components.campaignFooter.cta.candidateForDesktop')
          } else {
            return SCREEN_WIDTH.value < Breakpoints.md
              ? t('components.campaignFooter.cta.candidateForMobile')
              : t('components.campaignFooter.cta.candidateForFreeDesktop')
          }
        } else if (cost) {
          return SCREEN_WIDTH.value < Breakpoints.md
            ? t('components.campaignFooter.cta.addForMobile')
            : t('components.campaignFooter.cta.addForDesktop')
        } else {
          return SCREEN_WIDTH.value < Breakpoints.md
            ? t('components.campaignFooter.cta.addForMobile')
            : t('components.campaignFooter.cta.addForFreeDesktop')
        }
      }

    case 'seoList':
      return t('components.campaignFooter.cta.signUpToContactThem')

    default:
      return ''
  }
})

const informationTextContent = computed(() => {
  switch (props.page) {
    case 'homepage':
      return prosCountLoading.value
        ? ''
        : 'components.campaignFooter.informationTextFull.prosCount'

    case 'profile': {
      // TODO: check this logic - had to add in possible undefined values because influencer can be undefined
      const { contest, cost } = props.influencer || {
        contest: undefined,
        cost: undefined,
      }

      if (contest) {
        return SCREEN_WIDTH.value < Breakpoints.sm600
          ? 'components.campaignFooter.informationText.springboardMobile'
          : 'components.campaignFooter.informationText.springboardDesktop'
      } else if (cost) {
        const breakpoint = isUserLoggedOut.value
          ? Breakpoints.md
          : Breakpoints.sm600

        if (Number(cost) === 0) {
          return SCREEN_WIDTH.value < breakpoint
            ? 'components.campaignFooter.informationText.thisProIsFreeMobile'
            : 'components.campaignFooter.informationText.thisProIsFreeDesktop'
        } else {
          return SCREEN_WIDTH.value < breakpoint
            ? 'components.campaignFooter.informationText.thisProCostsMobile'
            : 'components.campaignFooter.informationText.thisProCostsDesktop'
        }
      }
      return ''
    }
    case 'seoList':
      return 'components.campaignFooter.informationText.seoList'

    default:
      return SCREEN_WIDTH.value < Breakpoints.md
        ? 'components.campaignFooter.informationText.oneProCostsMobile'
        : 'components.campaignFooter.informationText.oneProCostsDesktop'
  }
})

const isCartCounterDisplayed = computed<boolean>(() => {
  switch (props.page) {
    case 'homepage':
      return isDraftFilled.value
    case 'profile':
    case 'cart':
      return isDraftFilled.value || props.draftId !== 0
    case 'sendtrack':
    case 'shared-music':
      if (
        DRAFT_INFLUENCERS_COUNT.value === 0 &&
        IS_PROGRESSIVE_PROMOS_ENABLED.value
      )
        return false
      else return true

    default:
      return false
  }
})

const isDraftSavedEllipsis = computed<boolean>(() =>
  [t('components.campaignFooter.cta.candidateForMobile')].includes(
    ctaButtonText.value,
  ),
)

const isGroovizDisplayedInCta = computed<boolean>(() =>
  [
    t('components.campaignFooter.cta.addForDesktop'),
    t('components.campaignFooter.cta.addForMobile'),
    t('components.campaignFooter.cta.candidateForDesktop'),
    t('components.campaignFooter.cta.candidateForMobile'),
  ].includes(ctaButtonText.value),
)

const isGroovizDisplayedInInformationText = computed<boolean>(() =>
  [
    'components.campaignFooter.informationText.oneProCostsDesktop',
    'components.campaignFooter.informationText.oneProCostsMobile',
    'components.campaignFooter.informationText.thisProCostsDesktop',
    'components.campaignFooter.informationText.thisProCostsMobile',
  ].includes(informationTextContent.value),
)

const isGroovizHiddenInCartCounter = computed<boolean>(() => {
  switch (props.page) {
    case 'profile':
    case 'sendtrack':
    case 'shared-music':
      return isDraftEmpty.value

    default:
      return false
  }
})

const isInformationTextFullDisplayed = computed<boolean>(() => {
  if (isUserLoggedOut.value && SCREEN_WIDTH.value < Breakpoints.sm600) {
    return false
  } else {
    switch (props.page) {
      case 'homepage':
        return (
          isUserLoggedOut.value ||
          (SCREEN_WIDTH.value >= Breakpoints.sm600 &&
            isDraftEmpty.value &&
            props.draftId === 0)
        )
      case 'profile':
        return (
          isUserLoggedOut.value || (isDraftEmpty.value && props.draftId === 0)
        )

      case 'seoList':
        return true

      default:
        return false
    }
  }
})

const isInformationZoneDisplayed = computed<boolean>(() => {
  if (isUserLoggedOut.value) {
    return false
  } else {
    switch (props.page) {
      case 'profile':
        return (
          props.influencer?.contest !== null ||
          isDraftFilled.value ||
          props.draftId !== 0 ||
          SCREEN_WIDTH.value >= Breakpoints.md
        )
      case 'homepage':
        return SCREEN_WIDTH.value >= Breakpoints.sm600 || isDraftFilled.value
      case 'cart':
      case 'shared-music':
      case 'sendtrack':
        return true
      default:
        return false
    }
  }
})

const isProPictureDisplayed = computed<boolean>(
  () =>
    isCartCounterDisplayed.value &&
    DRAFT_INFLUENCERS.value.length === DRAFT_INFLUENCERS_COUNT.value &&
    SCREEN_WIDTH.value >= 480,
)

const isProgressivePromoNudgeDisplayed = computed<boolean>(() => {
    if(CART_PROMO_ID.value) return IS_PROGRESSIVE_PROMO.value
    
    return (
      (props.page === 'sendtrack' || 'cart') &&
      IS_PROGRESSIVE_PROMOS_ENABLED.value &&
      NEXT_PROMO.value !== null
    )
  }
)

const onlyCtaOnScreen = computed<boolean>(() => {
  switch (props.page) {
    case 'homepage':
      return isDraftEmpty.value
    case 'profile':
      return isUserLoggedOut.value
    case 'seoList':
      return true
    default:
      return false
  }
})

const slugOfTheLastProAddedToCart = computed<string | undefined>(() => {
  const lastProAddedToCart = GET_INF_BY_ID(
    props.draftId
      ? DRAFT_INFLUENCERS.value[DRAFT_INFLUENCERS.value.length - 1]
      : DRAFT_INFLUENCERS.value[DRAFT_INFLUENCERS.value.length - 1],
  )

  return lastProAddedToCart?.has_profile_picture
    ? lastProAddedToCart?.slug
    : undefined
})

const proPictureUrl = computed<string>(() =>
  pictureBuildHelper({
    kind: 'influencer',
    slug: slugOfTheLastProAddedToCart.value,
    size: '200_200',
    target: 'profile_picture',
  }),
)

const missingCuratorsCount = computed<number>(() => {
  if (!NEXT_PROMO.value) return 0
  return (
    NEXT_PROMO.value.progressive_parameters.min_influencers -
    DRAFT_INFLUENCERS_COUNT.value
  )
})

const discountPercent = computed<number>(
  () => NEXT_PROMO.value?.actions.campaign.value || 0,
)

// If there is a promo applied show the price after promo (code or progressive)
// If no promo show CART_COST
const displayedCost = computed<number>(() => {
  if (!DRAFT_ID.value) return DRAFT_COST.value
  if (CART_PROMO_ID.value && CAMPAIGN_COST_AFTER_PROMO.value !== CAMPAIGN_ITEM.value?.price)
    return CAMPAIGN_ITEM.value?.price_after_promo || 0
  if (!IS_PROGRESSIVE_PROMOS_ENABLED.value && !CART_PROMO_ID.value) return CAMPAIGN_ITEM.value?.price || 0
  return IS_PROGRESSIVE_PROMOS_ENABLED.value &&
    DRAFT_COST_AFTER_PROGRESSIVE_PROMO.value > 0
    ? DRAFT_COST_AFTER_PROGRESSIVE_PROMO.value
    : CART_COST.value
})

async function draftExistsInBackend(): Promise<boolean> {
  const drafts: DraftState[] = await coreFetch.$get(
    `/submission/draft/?band=${BAND_ID.value}`,
  )

  return drafts.some((draft) => draft.id === props.draftId)
}

function redirectToNextStep() {
  GO_TO_NEXT_STEP()
}

async function handleCTAClick(): Promise<void> {
  switch (props.page) {
    case 'seoList':
      BAND_SIGNUP_SET_DISPLAY(true)
      break

    case 'homepage':
      trackCampaignInitiated('home-footer-cta')
      await navigateTo(localePath('/draft/'))
      break

    case 'profile':
      if (isUserLoggedOut.value) {
        if (props.influencer?.id) await ADD_INF_TO_CART(props.influencer.id)

        BAND_SIGNUP_SET_DISPLAY(true)
      } else if (!currentProIsAdded.value) {
        if (
          props.influencer &&
          (MISMATCHES.value?.[props.influencer.id] ||
            DUPLICATE_SUBMISSIONS.value.includes(props.influencer.id))
        ) {
          SET_MISMATCH_MODAL({
            influencer: props.influencer,
            isDisplayed: true,
          })
        } else if (props.influencer?.id) {
          await ADD_INF_TO_CART(props.influencer.id)
        }

        if (props.draftId !== 0) {
          const storedWindowId = window.localStorage.getItem('windowId')
          const isAnotherTabOpen =
            storedWindowId && storedWindowId === 'catalog-window'

          // before going back to the catalog, check if the influencer store has "ranks" from the rec system
          // if not, it means the user has not seen the catalog yet, so we redirect him to the catalog without the "loaded" hash
          await navigateTo(
            {
              path: localePath(`/draft/${props.draftId}/influencers/`),
              hash:
                !isAnotherTabOpen && RANKED_INFLUENCERS.value.length > 0
                  ? '#loaded'
                  : undefined,
            },
            { replace: true },
          )

          const draftStillExists = await draftExistsInBackend()
          if (draftStillExists) return

          // if draft doesn't exist anymore, we redirect the user to the homepage
          await navigateTo(localePath('/'))
          CREATE_SNACKBAR({
            props: {
              text: t('draft.cannotAddCuratorToDeletedDraft') as string,
              icon: 'fas fa-circle-exclamation',
              link: 'draft',
            },
          })
        } else {
          PREVIOUS_URL.value.includes('/draft/influencers/')
            ? await navigateTo(localePath('/'))
            : await navigateToPreviousPage()
        }
      } else if (props.influencer?.id) {
        REMOVE_INF_FROM_CART(props.influencer?.id)
      }

      break

    case 'shared-music':
      await MARK_STEP_DONE(DRAFT_STEP.SHARED_MUSIC)
      redirectToNextStep()
      break

    case 'sendtrack':
      if (!route.path.includes('/agency/favorites'))
        await MARK_STEP_DONE(DRAFT_STEP.CURATORS)

      trackCuratorsSelectionValidated()
      redirectToNextStep()
      break

    case 'cart': {
      if (props.draftId === 0) {
        await navigateTo(localePath('/draft/'))
        break
      }

      await MARK_STEP_DONE(DRAFT_STEP.CURATORS)
      await navigateTo(localePath(GET_NEXT_STEP_URL()))
      break
    }
  }
}

onMounted(async () => {
  CHANGE_CAMPAIGN_FOOTER_DISPLAYED({
    isDisplayed: true,
    immediate: true,
  })

  if (props.page !== 'homepage') return

  prosCountLoading.value = true

  const res = await coreFetch.$get<Partial<{ count: number }>>(
    '/influencer/statsv3/?limit=1',
  )
  prosCount.value = res.count ?? 0

  prosCountLoading.value = false
})

onUnmounted(() => {
  // See the details of this action, its effect is delayed by a setTimeout. This is intended to prevent snackbar from changing position in a weird way when CampaignFooter disappear (after a page change for example)
  CHANGE_CAMPAIGN_FOOTER_DISPLAYED({ isDisplayed: false })
})
</script>

<template>
  <div class="campaignFooter">
    <div class="contentWrapper">
      <transition
        :name="`verticalTransition${DRAFT_INFLUENCERS_COUNT === 0 ? 'Reversed' : ''}`"
      >
        <div v-if="isInformationTextFullDisplayed" key="informationTextFull">
          <InformationTextFull>
            <div class="tw-flex">
              <i18n-t
                v-if="!prosCountLoading"
                :keypath="informationTextContent"
                :plural="prosCount"
                tag="span"
              >
                <template #breakLine><br class="md:tw-hidden" /></template>
                <template #curator
                  ><span class="tw-hidden md:tw-inline">{{
                    $t('common.curator')
                  }}</span></template
                >
                <template #count>{{ prosCount }}</template>
              </i18n-t>
              <GroovizAmount
                v-if="isGroovizDisplayedInInformationText"
                class="tw-ml-1"
                :amount="(influencer && influencer.cost) || 2"
                tag="span"
              />
            </div>
          </InformationTextFull>
        </div>
        <div
          v-else-if="isInformationZoneDisplayed"
          :key="`informationZone${
            isProPictureDisplayed ? 'WithProPicture' : ''
          }`"
          class="informationZone tw-flex tw-items-center tw-justify-start"
        >
          <transition name="verticalTransition">
            <ProPicture
              v-if="isProPictureDisplayed"
              :key="proPictureUrl"
              :url="proPictureUrl"
              :draft-id="draftId"
              class="tw-mr-4"
            />
          </transition>
          <div class="tw-flex tw-flex-col tw-gap-1">
            <CartCounter
              v-if="isCartCounterDisplayed"
              :displayed-cost="displayedCost"
              :campaign-cost="CAMPAIGN_ITEM?.price || 0"
              :influencers-count="DRAFT_INFLUENCERS_COUNT"
              :selected-wording="draftId !== 0"
              :hide-grooviz="isGroovizHiddenInCartCounter"
            />
            <CampaignFooterPromoNudge
              v-else-if="isProgressivePromoNudgeDisplayed"
              :missing-curators-count="missingCuratorsCount"
              :discount-percent="discountPercent"
              :is-semibold="!DRAFT_INFLUENCERS_COUNT"
            />
            <InformationText v-if="!DRAFT_INFLUENCERS_COUNT">
              <div class="tw-flex">
                <i18n-t
                  v-if="!prosCountLoading"
                  :keypath="informationTextContent"
                  :plural="prosCount"
                  tag="span"
                >
                  <template #breakLine><br class="md:tw-hidden" /></template>
                  <template #curator
                    ><span class="tw-hidden md:tw-inline"
                      >{{ $t('common.curator') }} /</span
                    ></template
                  >
                  <template #count>{{ prosCount }}</template>
                </i18n-t>
                <GroovizAmount
                  v-if="isGroovizDisplayedInInformationText"
                  class="tw-ml-1"
                  :amount="(influencer && influencer.cost) || 2"
                />
              </div>
            </InformationText>
            <div v-else class="tw-h-4">
              <transition name="slowFade">
                <DraftSavedMessage
                  v-if="SHOULD_DISPLAY_DRAFT_SAVED"
                  key="draftSavedMessage"
                  class="tw-absolute"
                  in-campaign-footer
                  :ellipsis="isDraftSavedEllipsis"
                />
                <CampaignFooterPromoNudge
                  v-else-if="isProgressivePromoNudgeDisplayed"
                  :missing-curators-count="missingCuratorsCount"
                  :discount-percent="discountPercent"
                />
                <VText
                  v-else-if="!IS_PROGRESSIVE_PROMOS_ENABLED"
                  cfg="sans/14/medium"
                  color="black"
                >
                  {{
                    $t(
                      `components.campaignFooter.resultsNudge.${
                        SCREEN_WIDTH > Breakpoints.md ? 'desktop' : 'mobile'
                      }`,
                    )
                  }}
                </VText>
              </transition>
            </div>
          </div>
        </div>
      </transition>

      <CTAButton
        class="cta tw-shrink-0"
        :class="{ onlyCtaOnScreen }"
        data-test-id="uiCampaignConfirmCTA"
        :kind="ctaButtonKind"
        :disabled="isCtaDisabled"
        @click="handleCTAClick"
      >
        <div class="tw-flex tw-justify-center">
          <span>
            {{ ctaButtonText }}
          </span>
          <GroovizAmount
            v-if="isGroovizDisplayedInCta"
            class="tw-ml-1"
            :amount="influencer?.cost || 2"
          />
        </div>
      </CTAButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.campaignFooter {
  @apply tw-fixed tw-bottom-0 tw-left-0 tw-flex tw-w-full tw-justify-center tw-bg-white;

  height: 72px;
  box-shadow: var(--shadow-regular-dark);
  @screen 375 {
    height: 83px;
  }

  .contentWrapper {
    @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-overflow-visible;

    max-width: var(--page-width);
    .cta {
      @apply tw-w-auto;
      &.onlyCtaOnScreen {
        @apply tw-w-full;
        @screen 600 {
          @apply tw-ml-6 tw-w-auto;
        }
      }
    }
  }
}

// TRANSITIONS
.verticalTransition-enter-active,
.verticalTransition-leave-active,
.verticalTransitionReversed-enter-active,
.verticalTransitionReversed-leave-active {
  transition:
    transform 0.3s,
    opacity 0.3s;
}
.verticalTransition-enter-active,
.verticalTransitionReversed-enter-active {
  position: absolute;
}
.verticalTransition-enter {
  transform: translateY(70px) translate3d(0, 0, 0);
  opacity: 0;
}
.verticalTransitionReversed-enter {
  transform: translateY(-70px) translate3d(0, 0, 0);
  opacity: 0;
}
.verticalTransition-enter-to,
.verticalTransitionReversed-enter-to {
  opacity: 1;
}
.verticalTransition-leave-to {
  transform: translateY(-70px) translate3d(0, 0, 0);
  opacity: 0;
}
.verticalTransitionReversed-leave-to {
  transform: translateY(70px) translate3d(0, 0, 0);
  opacity: 0;
}
</style>
